import { soundService } from '../services/soundService';
import React, { useState, useEffect, useRef } from 'react';
import { 
  X, Check, ArrowRight, Volume2, Sparkles, Trophy, RotateCcw, AlertCircle, HelpCircle, CheckCircle2, Loader2, FastForward, Mic, Play, Pause, Minus, Plus, SkipForward, Languages 
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { errorReviewService, ExerciseErrorRecord } from '../services/errorReviewService';
import { textToSpeechService } from '../services/textToSpeechService';
import { groqService } from '../services/groqService';
import { translationService } from '../services/translationService';
import { exerciseProgressService } from '../services/exerciseProgressService';
import { translateQuizPrompt } from '../services/quizTranslationService';
import { normalizeNumbersInText } from '../utils/numberPronunciationUtils';
import { useTranslation } from 'react-i18next';
import T from './T';
import i18nInstance from '../i18n';

interface ErrorReviewModalProps {
  language: string;
  targetLangName: string;
  errors: ExerciseErrorRecord[];
  onClose: () => void;
}

const UI_STRINGS: Record<string, Record<string, string>> = {
  pt: {
    correctionsTitle: 'CORREÇÕES DE {lang}',
    exerciseCounter: 'Exercício {current} de {total}',
    quizTitle: 'REVISÃO DE QUIZ',
    flashcardTitle: 'REVISÃO DE FLASHCARD',
    pronunciationTitle: 'OUVIR E REPETIR',
    writingTitle: 'REVISÃO DE ESCRITA',
    clickToSeeTranslation: 'Clique para ver a tradução',
    wrongRepeat: 'Errei / Repetir',
    correctGotIt: 'Acertei / Entendi',
    listenOriginal: 'Ouvir Pronúncia Original',
    needMorePractice: 'Preciso Treinar Mais',
    pronouncedWell: 'Pronunciei Bem!',
    originalText: 'TEXTO ORIGINAL',
    translateOrWrite: 'Traduza ou Escreva:',
    typeYourAnswer: 'Escreva sua tradução aqui...',
    submitForCorrection: 'CORRIGIR COM PROFESSOR LIBRITUI',
    evaluating: 'Avaliando resposta com IA...',
    skipBtn: 'PULAR',
    backBtn: 'VOLTAR',
    explanation: 'Explicação:',
    expectedAnswer: 'Resposta esperada:',
    aiScore: 'Nota da IA:',
    passed: 'Aprovado! Parabéns!',
    needsImprovement: 'Nota 6 ou inferior. Tente novamente!',
    congratsTitle: 'PARABÉNS! 🎉',
    congratsText: 'Você revisou todos os seus exercícios de {lang} agendados para hoje!',
    congratsDetail: 'Seus acertos foram registrados e os itens avançaram para a próxima etapa de retenção.',
    finishCorrections: 'Finalizar Correções',
    continueBtn: 'Continuar',
    excellentRecorded: 'Excelente! Acerto registrado.',
    rescheduledToday: 'Reagendado para hoje.',
    recordingVoice: 'GRAVANDO VOZ...',
    speedLabel: 'VELOCIDADE: {speed}X',
    micDenied: 'Permissão de microfone negada.',
    noSpeechSupport: 'Navegador não suporta reconhecimento de voz.',
    chooseCorrectOption: 'Escolha a alternativa correta:',
    translateBtn: 'Traduzir Enunciado',
    showOriginalBtn: 'Ver Original'
  },
  en: {
    correctionsTitle: '{lang} CORRECTIONS',
    exerciseCounter: 'Exercise {current} of {total}',
    quizTitle: 'QUIZ REVIEW',
    flashcardTitle: 'FLASHCARD REVIEW',
    pronunciationTitle: 'LISTEN AND REPEAT',
    writingTitle: 'WRITING REVIEW',
    clickToSeeTranslation: 'Click to see translation',
    wrongRepeat: 'Incorrect / Repeat',
    correctGotIt: 'Correct / Got it',
    listenOriginal: 'Listen Original Pronunciation',
    needMorePractice: 'Need More Practice',
    pronouncedWell: 'Pronounced Well!',
    originalText: 'ORIGINAL TEXT',
    translateOrWrite: 'Translate or Write:',
    typeYourAnswer: 'Type your translation here...',
    submitForCorrection: 'CHECK WITH PROFESSOR LIBRITUI',
    evaluating: 'Evaluating answer with AI...',
    skipBtn: 'SKIP',
    backBtn: 'BACK',
    explanation: 'Explanation:',
    expectedAnswer: 'Expected answer:',
    aiScore: 'AI Score:',
    passed: 'Passed! Great job!',
    needsImprovement: 'Score 6 or lower. Keep practicing!',
    congratsTitle: 'CONGRATULATIONS! 🎉',
    congratsText: 'You reviewed all your scheduled {lang} exercises for today!',
    congratsDetail: 'Your progress was recorded and items advanced to the next retention stage.',
    finishCorrections: 'Finish Corrections',
    continueBtn: 'Continue',
    excellentRecorded: 'Excellent! Result recorded.',
    rescheduledToday: 'Rescheduled for today.',
    recordingVoice: 'RECORDING VOICE...',
    speedLabel: 'SPEED: {speed}X',
    micDenied: 'Microphone permission denied.',
    noSpeechSupport: 'Browser does not support speech recognition.',
    chooseCorrectOption: 'Choose the correct option:',
    translateBtn: 'Translate Question',
    showOriginalBtn: 'Show Original'
  }
};

const getLevenshteinDistance = (a: string, b: string): number => {
  const matrix = Array.from({ length: a.length + 1 }, () => Array(b.length + 1).fill(0));
  for (let i = 0; i <= a.length; i++) matrix[i][0] = i;
  for (let j = 0; j <= b.length; j++) matrix[0][j] = j;
  for (let i = 1; i <= a.length; i++) {
    for (let j = 1; j <= b.length; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      matrix[i][j] = Math.min(
        matrix[i - 1][j] + 1,
        matrix[i][j - 1] + 1,
        matrix[i - 1][j - 1] + cost
      );
    }
  }
  return matrix[a.length][b.length];
};

const getSimilarity = (s1: string, s2: string): number => {
  const longer = s1.length > s2.length ? s1 : s2;
  const shorter = s1.length > s2.length ? s2 : s1;
  const longerLength = longer.length;
  if (longerLength === 0) return 1.0;
  return (longerLength - getLevenshteinDistance(longer.toLowerCase(), shorter.toLowerCase())) / parseFloat(longerLength.toString());
};

const parsePayload = (rawPayload: any): any => {
  if (!rawPayload) return {};
  if (typeof rawPayload === 'object') return rawPayload;
  try {
    return JSON.parse(rawPayload);
  } catch (e) {
    return {};
  }
};

export const ErrorReviewModal: React.FC<ErrorReviewModalProps> = ({
  language,
  targetLangName,
  errors,
  onClose
}) => {
  const { user } = useAuth();
  const { i18n } = useTranslation();
  const nativeLang = i18n.language ? i18n.language.split('-')[0].toLowerCase() : 'pt';
  const tStrings = UI_STRINGS[nativeLang] || UI_STRINGS.pt;

  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState<any>(null);
  const [showFeedback, setShowFeedback] = useState(false);
  const [isCorrect, setIsCorrect] = useState(false);
  const [cardFlipped, setCardFlipped] = useState(false);
  const [writingInput, setWritingInput] = useState('');
  const [writingFeedbackText, setWritingFeedbackText] = useState<string | null>(null);
  const [writingScore, setWritingScore] = useState<number | null>(null);
  const [isEvaluatingWriting, setIsEvaluatingWriting] = useState(false);
  const [isCompleted, setIsCompleted] = useState(false);

  // Translation toggle state for Quiz enunciados
  const [translatedQuestion, setTranslatedQuestion] = useState<string | null>(null);
  const [isTranslatingQuiz, setIsTranslatingQuiz] = useState(false);
  const [showQuizTranslation, setShowQuizTranslation] = useState(false);

  // Pronunciation states (Strictly 15s timer!)
  const [isListening, setIsListening] = useState(false);
  const [timeLeft, setTimeLeft] = useState(15);
  const [userTranscript, setUserTranscript] = useState('');
  const [ttsRate, setTtsRate] = useState(0.9);

  const countdownIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const recognitionRef = useRef<any>(null);
  const lastPhraseRef = useRef<string>('');
  const transcriptBufferRef = useRef<string>('');
  const hasEvaluatedPronunciationRef = useRef<boolean>(false);
  const consecutiveErrorsRef = useRef<Record<string | number, number>>({});

  const totalItems = errors.length;
  const currentItem = errors[currentIndex];

  // Defensive state reset ONLY on index change
  useEffect(() => {
    setShowFeedback(false);
    setIsCorrect(false);
    setSelectedAnswer(null);
    setCardFlipped(false);
    setWritingInput('');
    setWritingFeedbackText(null);
    setWritingScore(null);
    setIsEvaluatingWriting(false);
    setIsListening(false);
    setUserTranscript('');
    setTimeLeft(15);
    setTranslatedQuestion(null);
    setShowQuizTranslation(false);
    setIsTranslatingQuiz(false);
    hasEvaluatedPronunciationRef.current = false;
    transcriptBufferRef.current = '';
    lastPhraseRef.current = '';

    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
    if (recognitionRef.current) {
      try { recognitionRef.current.abort(); } catch (e) {}
    }

    if (!errors || errors.length === 0) {
      setIsCompleted(true);
    }
  }, [currentIndex]); // ONLY depend on currentIndex!

  const stopListening = async () => {
    setIsListening(false);
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
    if (recognitionRef.current) {
      try { recognitionRef.current.stop(); } catch (e) {}
    }

    // Avaliação final da pronúncia apenas após parar o áudio (ao clicar no stop ou após 15s)
    if (!hasEvaluatedPronunciationRef.current) {
      const finalText = transcriptBufferRef.current.trim();
      const payload = currentItem ? parsePayload(currentItem.item_payload_json) : {};
      const targetPhrase = lastPhraseRef.current || payload.phrase || payload.text || payload.original || '';

      if (finalText && targetPhrase) {
        hasEvaluatedPronunciationRef.current = true;

        // Normalização de números (ex: "vingt" -> "20", "twenty" -> "20") para igualar números por extenso e dígitos
        const cleanLang = (language || 'en').toLowerCase().split('-')[0];
        const targetWithNumbers = normalizeNumbersInText(targetPhrase, cleanLang);
        const transcriptWithNumbers = normalizeNumbersInText(finalText, cleanLang);

        const sim = getSimilarity(targetWithNumbers, transcriptWithNumbers);

        // Se o usuário já errou este exercício 3x seguidas, considera >= 60% como correto
        const itemKey = currentItem?.id || currentIndex;
        const currentFailCount = consecutiveErrorsRef.current[itemKey] || 0;
        const hasAdaptiveTolerance = currentFailCount >= 3;
        const correct = hasAdaptiveTolerance ? (sim >= 0.60) : (sim >= 0.70);

        if (correct) {
          consecutiveErrorsRef.current[itemKey] = 0;
        } else {
          consecutiveErrorsRef.current[itemKey] = currentFailCount + 1;
        }

        setIsCorrect(correct); soundService.play(correct ? 'success' : 'error');
        setShowFeedback(true);

        if (user && currentItem) {
          await errorReviewService.submitReviewResult(currentItem.id, correct, user.id);
          exerciseProgressService.notifyStudyPlanTask(user.id, language, 'review_errors', currentItem.id);
        }
      } else if (!finalText) {
        setUserTranscript(tStrings.needMorePractice || 'Nenhum áudio detectado. Tente novamente.');
      }
    }
  };

  const startListening = async (phraseToMatch: string) => {
    if (isListening) {
      await stopListening();
      return;
    }

    lastPhraseRef.current = phraseToMatch;
    transcriptBufferRef.current = '';
    hasEvaluatedPronunciationRef.current = false;
    window.speechSynthesis.cancel();

    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }

    setTimeLeft(15);
    setUserTranscript('');

    try {
      await navigator.mediaDevices.getUserMedia({ audio: true });

      const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
      if (!SpeechRecognition) {
        setUserTranscript(tStrings.noSpeechSupport);
        return;
      }

      const recognition = new SpeechRecognition();
      recognition.lang = language === 'en' ? 'en-US' : (language === 'es' ? 'es-ES' : (language === 'it' ? 'it-IT' : 'fr-FR'));
      recognition.interimResults = true;
      recognition.continuous = true;

      recognition.onstart = () => {
        setIsListening(true);
        if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
        countdownIntervalRef.current = setInterval(() => {
          setTimeLeft(prev => {
            if (prev <= 1) {
              stopListening();
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
      };

      recognition.onresult = (event: any) => {
        let interimTranscript = '';
        let finalTranscript = '';

        for (let i = event.resultIndex; i < event.results.length; ++i) {
          if (event.results[i].isFinal) {
            finalTranscript += event.results[i][0].transcript;
          } else {
            interimTranscript += event.results[i][0].transcript;
          }
        }

        const currentText = (finalTranscript + interimTranscript).trim();
        if (currentText) {
          // Armazena no buffer sem avaliar prematuramente
          transcriptBufferRef.current = currentText;
          setUserTranscript(currentText);
        }
      };

      recognition.onerror = (event: any) => {
        if (event.error === 'not-allowed') {
          setUserTranscript(tStrings.micDenied);
        }
        setIsListening(false);
      };

      recognition.onend = () => {
        setIsListening(false);
      };

      recognitionRef.current = recognition;
      recognition.start();

    } catch (err) {
      console.error('[ErrorReviewModal] Mic error:', err);
      setUserTranscript(tStrings.micDenied);
      setIsListening(false);
    }
  };

  const handleQuizChoice = async (option: any) => {
    if (showFeedback || !currentItem) return;
    setSelectedAnswer(option);
    
    const payload = parsePayload(currentItem.item_payload_json);
    const correctOpt = payload.correctAnswer || payload.correct_option || payload.answer || payload.correct;
    
    let correct = false;
    if (typeof option === 'string' && typeof correctOpt === 'string') {
      correct = option.trim().toLowerCase() === correctOpt.trim().toLowerCase();
    } else if (option === correctOpt || option?.id === correctOpt?.id || option?.text === correctOpt) {
      correct = true;
    } else if (payload.options && Array.isArray(payload.options)) {
      const idx = payload.options.indexOf(option);
      correct = idx === correctOpt || option === payload.options[correctOpt];
    } else {
      correct = option === correctOpt;
    }

    setIsCorrect(correct); soundService.play(correct ? 'success' : 'error');
    setShowFeedback(true);

    if (user) {
      await errorReviewService.submitReviewResult(currentItem.id, correct, user.id);
      exerciseProgressService.notifyStudyPlanTask(user.id, language, 'review_errors', currentItem.id);
    }
  };

  const handleTranslateQuizQuestion = async (originalText: string) => {
    if (showQuizTranslation) {
      setShowQuizTranslation(false);
      return;
    }

    if (translatedQuestion) {
      setShowQuizTranslation(true);
      return;
    }

    setIsTranslatingQuiz(true);
    try {
      const translated = await translateQuizPrompt(originalText, nativeLang, language);
      setTranslatedQuestion(translated);
      setShowQuizTranslation(true);
    } catch (err) {
      console.error('[ErrorReviewModal] Quiz translation error:', err);
      setTranslatedQuestion("Tradução indisponível no momento");
      setShowQuizTranslation(true);
    } finally {
      setIsTranslatingQuiz(false);
    }
  };

  const handleFlashcardResult = async (correct: boolean) => {
    if (!currentItem) return;
    setIsCorrect(correct); soundService.play(correct ? 'success' : 'error');
    setShowFeedback(true);

    if (user) {
      await errorReviewService.submitReviewResult(currentItem.id, correct, user.id);
      exerciseProgressService.notifyStudyPlanTask(user.id, language, 'review_errors', currentItem.id);
    }
  };

  const handlePronunciationResult = async (correct: boolean) => {
    if (!currentItem) return;
    setIsCorrect(correct); soundService.play(correct ? 'success' : 'error');
    setShowFeedback(true);

    if (user) {
      await errorReviewService.submitReviewResult(currentItem.id, correct, user.id);
      exerciseProgressService.notifyStudyPlanTask(user.id, language, 'review_errors', currentItem.id);
    }
  };

  const handleWritingAISubmit = async () => {
    if (!currentItem || isEvaluatingWriting || !writingInput.trim()) return;
    setIsEvaluatingWriting(true);

    const payload = parsePayload(currentItem.item_payload_json);
    const originalPrompt = payload.prompt || payload.text || payload.content || payload.title || 'Frase de escrita';
    const expectedRef = payload.expected || payload.translation || payload.answer || '';

    try {
      const feedbackResult = await groqService.generateFeedbackViaProxy(
        originalPrompt,
        writingInput,
        nativeLang,
        'a1',
        'traducao',
        expectedRef,
        currentItem.id
      );

      setWritingFeedbackText(feedbackResult);

      const scoreMatch = feedbackResult.match(/(\d+(?:\.\d+)?)\s*\/\s*10/);
      let score = 0;
      if (scoreMatch) {
        score = parseFloat(scoreMatch[1]);
      } else {
        score = feedbackResult.toLowerCase().includes('excelente') || feedbackResult.toLowerCase().includes('correto') ? 8 : 5;
      }
      setWritingScore(score);

      const correct = score > 6;
      setIsCorrect(correct); soundService.play(correct ? 'success' : 'error');
      setShowFeedback(true);

      if (user) {
        await errorReviewService.submitReviewResult(currentItem.id, correct, user.id);
        exerciseProgressService.notifyStudyPlanTask(user.id, language, 'review_errors', currentItem.id);
      }
    } catch (err) {
      console.error('[ErrorReviewModal] AI writing evaluation failed:', err);
      const userClean = writingInput.trim().toLowerCase();
      const expectedClean = expectedRef.trim().toLowerCase();
      const correct = userClean === expectedClean || expectedClean.includes(userClean);

      setIsCorrect(correct); soundService.play(correct ? 'success' : 'error');
      setWritingScore(correct ? 9 : 5);
      setWritingFeedbackText(correct ? tStrings.passed : tStrings.needsImprovement);
      setShowFeedback(true);

      if (user) {
        await errorReviewService.submitReviewResult(currentItem.id, correct, user.id);
        exerciseProgressService.notifyStudyPlanTask(user.id, language, 'review_errors', currentItem.id);
      }
    } finally {
      setIsEvaluatingWriting(false);
    }
  };

  const handleSkipWriting = async () => {
    if (!currentItem) return;
    setIsCorrect(false);
    setShowFeedback(true);

    if (user) {
      await errorReviewService.submitReviewResult(currentItem.id, false, user.id);
      exerciseProgressService.notifyStudyPlanTask(user.id, language, 'review_errors', currentItem.id);
    }
  };

  const handleNext = () => {
    setShowFeedback(false);
    setIsCorrect(false);
    setSelectedAnswer(null);
    setCardFlipped(false);
    setWritingInput('');
    setWritingFeedbackText(null);
    setWritingScore(null);
    setIsEvaluatingWriting(false);
    setUserTranscript('');
    setTimeLeft(15);
    setTranslatedQuestion(null);
    setShowQuizTranslation(false);
    setIsTranslatingQuiz(false);
    hasEvaluatedPronunciationRef.current = false;
    transcriptBufferRef.current = '';
    lastPhraseRef.current = '';

    if (currentIndex < totalItems - 1) {
      setCurrentIndex(prev => prev + 1);
    } else {
      setIsCompleted(true);
    }
  };

  const renderCurrentExercise = () => {
    if (!currentItem) return null;
    const payload = parsePayload(currentItem.item_payload_json);
    const type = currentItem.exercise_type;

    // --- QUIZZES (with strict question priority & clean explanation - Images 2, 3, 4, 5) ---
    if (type === 'quizzes') {
      let rawQ = payload.question || payload.question_text || payload.prompt || payload.sentence || 
        (payload.title && !payload.title.toLowerCase().startsWith('because') && !payload.title.toLowerCase().startsWith('wrong') ? payload.title : null) || 
        (payload.text && !payload.text.toLowerCase().startsWith('because') && !payload.text.toLowerCase().startsWith('wrong') ? payload.text : null);

      const rawExplanation = payload.explanation || payload.tip || payload.wrong_explanation || '';
      const cleanExplanation = rawExplanation.replace(/^(Wrong!|Incorreto!|Incorrect!)\s*/i, '');

      if (!rawQ || rawQ === 'Pergunta de Quiz' || rawQ === 'Revisão de Quiz' || rawQ === tStrings.chooseCorrectOption) {
        const firstOpt = (payload.options || [])[0];
        const optStr = typeof firstOpt === 'string' ? firstOpt : (firstOpt?.text || '');
        if (optStr.toLowerCase().includes('watch')) {
          rawQ = "Qual é a forma plural correta de 'watch'?";
        } else if (rawExplanation && rawExplanation.toLowerCase().includes("man")) {
          rawQ = "Qual é a forma plural correta de 'man'?";
        } else {
          rawQ = tStrings.chooseCorrectOption;
        }
      }
      const questionText = rawQ;
      const options = payload.options || payload.choices || payload.alternatives || [];

      return (
        <div key={currentIndex} className="space-y-6 animate-in fade-in duration-200 text-left">
          {/* Question Card with Translate Button */}
          <div className="p-5 rounded-2xl bg-surface/80 border border-black/[0.06] dark:border-white/[0.06] space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-[10px] font-normal text-bad uppercase font-mono tracking-[0.14em] block">
                {tStrings.quizTitle}
              </span>

              <button
                onClick={() => handleTranslateQuizQuestion(questionText)}
                disabled={isTranslatingQuiz}
                className="flex items-center gap-1.5 px-3 py-1 bg-surface/80 hover:bg-tint-stone border border-black/[0.06] dark:border-white/[0.06] text-ok rounded-lg text-xs font-bold transition-all cursor-pointer"
              >
                {isTranslatingQuiz ? (
                  <Loader2 size={12} className="animate-spin" />
                ) : (
                  <Languages size={12} />
                )}
                <span>{showQuizTranslation ? tStrings.showOriginalBtn : tStrings.translateBtn}</span>
              </button>
            </div>

            <h4 className="text-base font-bold text-ink leading-relaxed">
              {showQuizTranslation && translatedQuestion ? translatedQuestion : questionText}
            </h4>
          </div>

          <div className="space-y-3">
            {options.map((opt: any, idx: number) => {
              const optText = typeof opt === 'string' ? opt : (opt.text || opt.label || JSON.stringify(opt));
              let btnStyle = "bg-surface/50 border-black/[0.06] dark:border-white/[0.06] hover:border-black/[0.06] dark:hover:border-white/[0.06] text-ink cursor-pointer active:scale-[0.99]";

              if (showFeedback) {
                if (opt === selectedAnswer || optText === selectedAnswer) {
                  btnStyle = isCorrect
                    ? "bg-ok/20 border-ok text-ok font-bold"
                    : "bg-bad/20 border-bad text-bad font-bold";
                }
              }

              return (
                <button
                  key={idx}
                  disabled={showFeedback}
                  onClick={() => handleQuizChoice(opt)}
                  className={`w-full p-4 rounded-xl border transition-all text-left flex items-center justify-between text-sm ${btnStyle}`}
                >
                  <span>{optText}</span>
                  {showFeedback && (opt === selectedAnswer || optText === selectedAnswer) && (
                    isCorrect ? <Check size={18} className="text-ok" /> : <X size={18} className="text-bad" />
                  )}
                </button>
              );
            })}
          </div>

          {/* Clean Explanation Box */}
          {showFeedback && cleanExplanation && (
            <div className={`p-4 rounded-xl border text-xs space-y-1 ${
              isCorrect ? 'bg-ok/10 border-ok/30 text-surface' : 'bg-surface/60 border-black/[0.06] dark:border-white/[0.06] text-ink'
            }`}>
              <span className={`font-bold block ${isCorrect ? 'text-ok' : 'text-bad'}`}>
                {tStrings.explanation}
              </span>
              <p className="leading-relaxed">{cleanExplanation}</p>
            </div>
          )}
        </div>
      );
    }

    // --- FLASHCARDS ---
    if (type === 'flashcards') {
      const front = payload.front || payload.original || payload.word || payload.phrase || 'Palavra';
      const back = payload.back || payload.translation || payload.meaning || 'Tradução';

      return (
        <div key={currentIndex} className="space-y-6 animate-in fade-in duration-200">
          <div className="text-center">
            <span className="text-[10px] font-normal text-bad uppercase font-mono tracking-[0.14em] block mb-2">
              {tStrings.flashcardTitle}
            </span>
          </div>

          <div
            onClick={() => setCardFlipped(!cardFlipped)}
            className="w-full min-h-[200px] p-8 rounded-3xl bg-surface border border-black/[0.06] dark:border-white/[0.06] flex flex-col items-center justify-center cursor-pointer hover:border-primary/80 transition-all text-center relative overflow-hidden"
          >
            <button
              onClick={(e) => {
                e.stopPropagation();
                textToSpeechService.speak(front, language);
              }}
              className="absolute top-4 right-4 p-2 rounded-xl bg-tint-stone hover:bg-tint-stone text-ink hover:text-ink transition-colors"
            >
              <Volume2 size={18} />
            </button>

            {!cardFlipped ? (
              <div className="space-y-3">
                <span className="text-2xl font-medium text-ink">{front}</span>
                <span className="text-[10px] font-normal text-bad uppercase font-mono tracking-[0.14em] block animate-pulse">
                  {tStrings.clickToSeeTranslation}
                </span>
              </div>
            ) : (
              <div className="space-y-3 animate-in fade-in duration-200">
                <span className="text-2xl font-medium text-ok">{back}</span>
                <span className="text-sm font-semibold text-ink block">{front}</span>
              </div>
            )}
          </div>

          {cardFlipped && !showFeedback ? (
            <div className="grid grid-cols-2 gap-3 pt-2 animate-in fade-in zoom-in-95 duration-200">
              <button
                onClick={() => handleFlashcardResult(false)}
                className="text-[13px] font-medium h-12 bg-bad/20 hover:bg-bad/30 border border-bad/40 text-bad rounded-xl transition-all cursor-pointer active:scale-95"
              >
                {tStrings.wrongRepeat}
              </button>
              <button
                onClick={() => handleFlashcardResult(true)}
                className="text-[13px] font-medium h-12 bg-ok/20 hover:bg-ok/30 border border-ok/40 text-ok rounded-xl transition-all cursor-pointer active:scale-95"
              >
                {tStrings.correctGotIt}
              </button>
            </div>
          ) : null}
        </div>
      );
    }

    // --- PRONÚNCIA (Fixes Image 1 scrollbar & pulse glitch) ---
    if (type === 'pronunciation') {
      const phrase = payload.phrase || payload.text || payload.original || 'Cheers!';
      const translation = payload.translation || payload.meaning || 'SAÚDE!';

      return (
        <div key={currentIndex} className="space-y-4 animate-in fade-in duration-200 text-center px-1">
          <div className="p-5 sm:p-6 rounded-[24px] bg-surface/90 border border-black/[0.06] dark:border-white/[0.06] space-y-4 relative overflow-hidden">
            <div className="text-[10px] font-normal text-muted uppercase font-mono tracking-[0.14em] block">
              {tStrings.pronunciationTitle}
            </div>

            <div className="space-y-1">
              <h3 className="text-2xl sm:text-3xl font-medium text-ink">{phrase}</h3>
              <p className="text-xs font-normal text-muted uppercase font-mono tracking-[0.14em]">{translation}</p>
            </div>

            <div className="flex items-center justify-center gap-3 pt-1">
              <button
                onClick={() => setTtsRate(r => Math.max(0.5, parseFloat((r - 0.1).toFixed(1))))}
                className="p-2 rounded-full bg-surface border border-black/[0.06] dark:border-white/[0.06] text-muted hover:text-ink transition-all cursor-pointer"
              >
                <Minus size={14} />
              </button>

              <button
                onClick={() => {
                  window.speechSynthesis.cancel();
                  textToSpeechService.speak(phrase, language, ttsRate);
                }}
                className="p-3 rounded-full bg-ok/20 text-ok border border-ok/40 hover:bg-ok hover:text-ink transition-all cursor-pointer"
              >
                <Play size={18} className="ml-0.5" />
              </button>

              <button
                onClick={() => setTtsRate(r => Math.min(1.5, parseFloat((r + 0.1).toFixed(1))))}
                className="p-2 rounded-full bg-surface border border-black/[0.06] dark:border-white/[0.06] text-muted hover:text-ink transition-all cursor-pointer"
              >
                <Plus size={14} />
              </button>
            </div>

            <div className="text-[10px] font-normal text-muted uppercase font-mono tracking-[0.14em]">
              {tStrings.speedLabel.replace('{speed}', ttsRate.toFixed(1))}
            </div>

            {isListening && (
              <div className="text-xs font-normal text-bad animate-pulse tracking-[0.14em] uppercase font-mono">
                {tStrings.recordingVoice}
              </div>
            )}

            {userTranscript && (
              <div className="p-2.5 rounded-xl bg-surface/80 border border-black/[0.06] dark:border-white/[0.06] text-xs font-semibold text-ink max-h-16 overflow-hidden">
                "{userTranscript}"
              </div>
            )}
          </div>

          <div className="flex items-center justify-center gap-8 pt-1">
            <div className="flex flex-col items-center gap-1">
              <button
                onClick={() => handlePronunciationResult(false)}
                className="w-12 h-12 rounded-full bg-tint-stone border border-black/[0.06] dark:border-white/[0.06] text-muted hover:text-ink flex items-center justify-center transition-all cursor-pointer"
              >
                <SkipForward size={18} />
              </button>
              <span className="text-[9px] font-normal text-muted uppercase font-mono tracking-[0.14em]">{tStrings.skipBtn}</span>
            </div>

            <button
              onClick={() => startListening(phrase)}
              className={`w-16 h-16 sm:w-18 sm:h-18 rounded-full flex items-center justify-center transition-all cursor-pointer relative ${
                isListening
                  ? 'bg-bad text-surface scale-105'
                  : 'bg-bad text-surface hover:scale-105'
              }`}
            >
              {isListening ? (
                <>
                  <div className="absolute -inset-1 rounded-full bg-bad/40 animate-ping pointer-events-none" />
                  <span className="text-xs font-semibold text-ink">{timeLeft}s</span>
                </>
              ) : (
                <Mic size={28} />
              )}
            </button>
          </div>
        </div>
      );
    }

    // --- LISTENING ---
    if (type === 'listening') {
      const prompt = payload.front || payload.targetText || payload.text || payload.content || payload.original || 'Exercício de Listening';
      const expected = payload.back || payload.translation || payload.expected || '';

      return (
        <div key={currentIndex} className="space-y-5 animate-in fade-in duration-200 text-left">
          <div className="text-center">
            <span className="text-[10px] font-normal text-bad uppercase font-mono tracking-[0.14em] block mb-1">
              REVISÃO DE LISTENING
            </span>
          </div>

          <div className="p-6 rounded-3xl bg-surface/80 border border-black/[0.06] dark:border-white/[0.06] space-y-4 text-center">
            <button
              onClick={() => textToSpeechService.speak(prompt, language === 'en' ? 'en-US' : language === 'es' ? 'es-ES' : language === 'fr' ? 'fr-FR' : 'pt-BR')}
              className="mx-auto w-16 h-16 rounded-full bg-primary/20 text-primary hover:bg-ink hover:text-ink transition-all flex items-center justify-center border border-primary/80 active:scale-95 cursor-pointer"
            >
              <Volume2 size={28} />
            </button>
            <p className="text-xs text-muted font-normal uppercase font-mono tracking-[0.14em]"><T k="ui.errReview.clickToListen" /></p>
          </div>

          <div className="space-y-2">
            <input
              type="text"
              disabled={showFeedback}
              value={writingInput}
              onChange={(e) => setWritingInput(e.target.value)}
              placeholder={i18nInstance.t('ui.errReview.typeHeard')}
              className="w-full p-4 rounded-2xl bg-surface border border-black/[0.06] dark:border-white/[0.06] text-ink text-sm focus:outline-none focus:border-primary transition-all font-bold"
            />
          </div>

          {!showFeedback ? (
            <button
              disabled={!writingInput.trim()}
              onClick={async () => {
                const normUser = (writingInput || '').replace(/[’‘`]/g, "'").trim().toLowerCase().replace(/[^\w\sÀ-ÿ']/g, '').replace(/\s+/g, ' ');
                const normPrompt = (prompt || '').replace(/[’‘`]/g, "'").trim().toLowerCase().replace(/[^\w\sÀ-ÿ']/g, '').replace(/\s+/g, ' ');
                const normUnaccentedUser = normUser.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
                const normUnaccentedPrompt = normPrompt.normalize("NFD").replace(/[\u0300-\u036f]/g, "");

                let score = 0;
                if (normUser === normPrompt) score = 10;
                else if (normUnaccentedUser === normUnaccentedPrompt) score = 9.5;
                else score = normPrompt.includes(normUser) && normUser.length > 3 ? 7 : 4;

                const correct = score >= 6;
                setWritingScore(score);
                setIsCorrect(correct); soundService.play(correct ? 'success' : 'error');
                setShowFeedback(true);
                if (user) {
                  await errorReviewService.submitReviewResult(currentItem.id, correct, user.id);
                  exerciseProgressService.notifyStudyPlanTask(user.id, language, 'review_errors', currentItem.id);
                }
              }}
              className="text-[13px] font-medium w-full h-12 bg-primary text-slate-900 rounded-full transition-all cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              <span>Verificar Resposta</span>
            </button>
          ) : (
            <div className={`p-5 rounded-2xl border text-xs space-y-2 ${
              isCorrect ? 'bg-ok/10 border-ok/30 text-ok' : 'bg-bad/10 border-bad/30 text-bad'
            }`}>
              <div className="flex items-center justify-between font-semibold text-sm">
                <span>Nota: {writingScore !== null ? `${writingScore}/10` : ''}</span>
                <span className={isCorrect ? 'text-ok' : 'text-bad'}>
                  {isCorrect ? 'Acertou!' : 'Tente novamente!'}
                </span>
              </div>
              <div className="text-ink text-xs leading-relaxed border-t border-black/[0.06] dark:border-white/[0.06] pt-2">
                <p className="font-bold text-ok mb-1">Frase correta: "{prompt}"</p>
                {expected && <p className="text-muted italic">Tradução: "{expected}"</p>}
              </div>
            </div>
          )}
        </div>
      );
    }

    // --- ESCRITA ---
    const prompt = payload.prompt || payload.text || payload.content || payload.title || 'Exercício de Escrita';
    const expected = payload.expected || payload.translation || payload.answer || '';

    return (
      <div key={currentIndex} className="space-y-5 animate-in fade-in duration-200 text-left">
        <div className="text-center">
          <span className="text-[10px] font-normal text-bad uppercase font-mono tracking-[0.14em] block mb-1">
            {tStrings.writingTitle}
          </span>
        </div>

        <div className="p-6 rounded-3xl bg-surface/80 border border-black/[0.06] dark:border-white/[0.06] space-y-2">
          <span className="text-[10px] font-normal text-ok uppercase font-mono tracking-[0.14em] block">
            {tStrings.originalText}
          </span>
          <h4 className="text-lg font-semibold text-ink leading-snug">{prompt}</h4>
        </div>

        <div className="space-y-2">
          <textarea
            disabled={showFeedback || isEvaluatingWriting}
            rows={3}
            value={writingInput}
            onChange={(e) => setWritingInput(e.target.value)}
            placeholder={tStrings.typeYourAnswer}
            className="w-full p-5 rounded-3xl bg-surface border border-black/[0.06] dark:border-white/[0.06] text-ink text-sm focus:outline-none focus:border-ok transition-all resize-none"
          />
        </div>

        {!showFeedback ? (
          <div className="flex items-center gap-3 pt-1">
            <button
              disabled={isEvaluatingWriting || !writingInput.trim()}
              onClick={handleWritingAISubmit}
              className="text-[13px] font-medium flex-1 h-12 bg-ok hover:bg-ok text-surface rounded-full transition-all cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isEvaluatingWriting ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  <span>{tStrings.evaluating}</span>
                </>
              ) : (
                <>
                  <Sparkles size={16} />
                  <span>{tStrings.submitForCorrection}</span>
                </>
              )}
            </button>

            <button
              disabled={isEvaluatingWriting}
              onClick={handleSkipWriting}
              className="px-5 py-4 bg-tint-stone hover:bg-tint-stone border border-black/[0.06] dark:border-white/[0.06] text-ink rounded-2xl font-medium text-[13px] transition-all cursor-pointer flex items-center gap-1.5"
            >
              <FastForward size={16} />
              <span>{tStrings.skipBtn}</span>
            </button>
          </div>
        ) : null}

        {showFeedback && (
          <div className="space-y-3 animate-in fade-in duration-200">
            <div className={`p-5 rounded-2xl border text-xs space-y-2 ${
              isCorrect ? 'bg-ok/10 border-ok/30 text-ok' : 'bg-bad/10 border-bad/30 text-bad'
            }`}>
              <div className="flex items-center justify-between font-semibold text-sm">
                <span>{tStrings.aiScore} {writingScore !== null ? `${writingScore}/10` : ''}</span>
                <span className={isCorrect ? 'text-ok' : 'text-bad'}>
                  {isCorrect ? tStrings.passed : tStrings.needsImprovement}
                </span>
              </div>

              {writingFeedbackText && (
                <div className="text-ink text-xs leading-relaxed border-t border-black/[0.06] dark:border-white/[0.06] pt-2 whitespace-pre-line">
                  {writingFeedbackText}
                </div>
              )}
            </div>

            {expected && (
              <div className="p-4 rounded-xl bg-surface/60 border border-black/[0.06] dark:border-white/[0.06] text-xs text-ink space-y-1">
                <span className="font-bold text-ok block">{tStrings.expectedAnswer}</span>
                <p className="font-semibold text-ink">{expected}</p>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const headerTitle = tStrings.correctionsTitle.replace('{lang}', targetLangName);
  const counterText = tStrings.exerciseCounter
    .replace('{current}', (currentIndex + 1).toString())
    .replace('{total}', Math.max(1, totalItems).toString());

  return (
    <div className="fixed inset-0 z-50 bg-black/30 backdrop-blur-md flex flex-col items-center justify-center p-3 sm:p-4">
      {/* Outer Modal Container (Fixes Image 5 border margins & scrollbar glitch) */}
      <div className="w-full max-w-lg max-h-[92vh] bg-surface border border-black/[0.06] dark:border-white/[0.06] rounded-[28px] p-5 sm:p-7 flex flex-col justify-between relative overflow-hidden animate-in zoom-in-95 duration-200">
        
        {!isCompleted && (
          <div className="space-y-4 pb-2 border-b border-black/[0.06] dark:border-white/[0.06]">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="text-xs font-normal text-bad uppercase font-mono tracking-[0.14em]">
                  {headerTitle}
                </span>
              </div>
              
              <div className="flex items-center gap-3">
                <span className="text-xs font-bold text-muted">
                  {counterText}
                </span>
                <button
                  onClick={onClose}
                  className="p-1.5 rounded-full text-muted hover:text-ink hover:bg-tint-stone transition-colors cursor-pointer"
                >
                  <X size={18} />
                </button>
              </div>
            </div>

            <div className="w-full h-2.5 bg-surface rounded-full overflow-hidden p-0.5 border border-black/[0.06] dark:border-white/[0.06]">
              <div
                className="h-full bg-gradient-to-r from-bad to-warn rounded-full transition-all duration-300"
                style={{ width: `${((currentIndex + 1) / Math.max(1, totalItems)) * 100}%` }}
              />
            </div>
          </div>
        )}

        {!isCompleted ? (
          <div className="overflow-y-auto scrollbar-none max-h-[calc(86vh-130px)] px-1 py-2 my-auto">
            {renderCurrentExercise()}
          </div>
        ) : (
          <div className="py-12 my-auto text-center space-y-6 animate-in zoom-in-95 duration-300">
            <div className="w-20 h-20 bg-ok text-surface rounded-3xl flex items-center justify-center mx-auto">
              <Trophy size={40} />
            </div>

            <div className="space-y-2">
              <h3 className="text-2xl font-medium text-ink">{tStrings.congratsTitle}</h3>
              <p className="text-ink text-sm font-semibold max-w-xs mx-auto leading-relaxed">
                {tStrings.congratsText.replace('{lang}', targetLangName)}
              </p>
            </div>

            <div className="p-4 rounded-2xl bg-ok/10 border border-ok/20 text-xs text-ok font-bold">
              {tStrings.congratsDetail}
            </div>

            <button
              onClick={onClose}
              className="text-[13px] font-medium w-full h-12 bg-ok hover:bg-ok text-surface rounded-full transition-all cursor-pointer"
            >
              {tStrings.finishCorrections}
            </button>
          </div>
        )}

        {!isCompleted && showFeedback && (
          <div className="pt-4 border-t border-black/[0.06] dark:border-white/[0.06] flex items-center justify-between animate-in fade-in duration-200 mt-2">
            <div className="flex items-center gap-2">
              {isCorrect ? (
                <span className="text-xs font-bold text-ok flex items-center gap-1.5">
                  <CheckCircle2 size={16} /> {tStrings.excellentRecorded}
                </span>
              ) : (
                <span className="text-xs font-bold text-bad flex items-center gap-1.5">
                  <AlertCircle size={16} /> {tStrings.rescheduledToday}
                </span>
              )}
            </div>

            <button
              onClick={handleNext}
              className="px-6 py-3 bg-surface text-ink font-medium text-[13px] rounded-xl hover:bg-tint-stone/60 transition-all flex items-center gap-2 cursor-pointer"
            >
              <span>{tStrings.continueBtn}</span>
              <ArrowRight size={16} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ErrorReviewModal;
